/**
 * @module browser-storage-adapter
 * @description Implements the local-first authentication adapter used for
 * demos, prototyping, and Storybook scenarios. The adapter persists users and
 * sessions to guarded browser storage while conforming to the shared auth contract.
 */

import {
  type BrowserStorage,
  getBrowserLocalStorage,
  isBrowserStorageQuotaError,
} from "../browser-storage";
import { AuthError } from "../errors";
import { warnRuntimeAdapterMock } from "../runtime-warning";
import type { AuthSession, AuthUser } from "../types";
import type { AuthAdapter } from "./interface";

interface StoredUser extends AuthUser {
  passwordHash: string;
  email: string;
  isBanned?: boolean;
}

const USERS_KEY = "geenius_users";
const SESSION_KEY = "geenius_session";
const EMAIL_ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOwn(value: Record<string, unknown>, key: string): boolean {
  return Object.hasOwn(value, key);
}

function parseSessionExpiry(value: string): Date | null {
  const expiry = new Date(value);
  return Number.isNaN(expiry.getTime()) ? null : expiry;
}

function isAuthUser(value: unknown): value is AuthUser {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    (value.email === undefined || typeof value.email === "string") &&
    (value.name === undefined || typeof value.name === "string") &&
    (value.image === undefined || typeof value.image === "string") &&
    (value.role === undefined || typeof value.role === "string") &&
    (value.isBanned === undefined || typeof value.isBanned === "boolean") &&
    (value.createdAt === undefined || typeof value.createdAt === "string")
  );
}

function isStoredUser(value: unknown): value is StoredUser {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    (value.email === undefined || typeof value.email === "string") &&
    (value.name === undefined || typeof value.name === "string") &&
    (value.image === undefined || typeof value.image === "string") &&
    (value.role === undefined || typeof value.role === "string") &&
    (value.createdAt === undefined || typeof value.createdAt === "string") &&
    (value.isBanned === undefined || typeof value.isBanned === "boolean") &&
    typeof value.email === "string" &&
    typeof value.passwordHash === "string"
  );
}

function isAuthSession(value: unknown): value is AuthSession {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.userId === "string" &&
    hasOwn(value, "user") &&
    (value.sessionToken === undefined ||
      typeof value.sessionToken === "string") &&
    (value.expiresAt === undefined ||
      (typeof value.expiresAt === "string" &&
        parseSessionExpiry(value.expiresAt) !== null)) &&
    (value.user === null || isAuthUser(value.user))
  );
}

function parseStoredUsers(raw: string | null): StoredUser[] {
  const parsed: unknown = JSON.parse(raw || "[]");
  if (!Array.isArray(parsed) || !parsed.every(isStoredUser)) {
    throw new AuthError(
      "Invalid users payload in browser storage",
      "SERVICE_ERROR",
    );
  }
  return parsed;
}

function parseStoredSession(raw: string): AuthSession {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    throw new AuthError(
      "Failed to parse session from browser storage",
      "SERVICE_ERROR",
      error,
    );
  }
  if (!isAuthSession(parsed)) {
    throw new AuthError(
      "Invalid session payload in browser storage",
      "SERVICE_ERROR",
    );
  }
  return parsed;
}

function readStoredSession(storage: BrowserStorage, raw: string): AuthSession {
  try {
    return parseStoredSession(raw);
  } catch (error) {
    storage.removeItem(SESSION_KEY);
    throw error;
  }
}

function getUsers(): StoredUser[] {
  try {
    const storage = getBrowserLocalStorage();
    return parseStoredUsers(storage.getItem(USERS_KEY));
  } catch (error) {
    if (error instanceof AuthError) {
      throw error;
    }
    if (error instanceof SyntaxError) {
      throw new AuthError(
        "Failed to parse users from browser storage",
        "SERVICE_ERROR",
        error,
      );
    }
    throw new AuthError(
      "Failed to read from browser storage",
      "SERVICE_ERROR",
      error,
    );
  }
}

function saveUsers(users: StoredUser[]): void {
  try {
    const storage = getBrowserLocalStorage();
    storage.setItem(USERS_KEY, JSON.stringify(users));
  } catch (error) {
    if (isBrowserStorageQuotaError(error)) {
      throw new AuthError(
        "browser storage quota exceeded",
        "SERVICE_ERROR",
        error,
      );
    }
    throw new AuthError(
      "Failed to write to browser storage",
      "SERVICE_ERROR",
      error,
    );
  }
}

// NOTE: This is intentionally a weak hash — it is a mock implementation for
// prototyping only (Pronto tier). Real auth adapters use proper password
// hashing on the server side.
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function assertValidCredentials(email: string, password: string): string {
  const normalizedEmail = normalizeEmail(email);
  if (!EMAIL_ADDRESS_PATTERN.test(normalizedEmail) || password.trim() === "") {
    throw new AuthError("Invalid email or password", "INVALID_CREDENTIALS");
  }
  return normalizedEmail;
}

function assertUserCanAuthenticate(user: StoredUser): void {
  if (user.isBanned === true) {
    throw new AuthError("User is banned", "UNAUTHORIZED");
  }
}

function toAuthUser(user: StoredUser): AuthUser {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    image: user.image,
    role: user.role,
    createdAt: user.createdAt,
  };
}

function saveSession(session: AuthSession): AuthSession {
  try {
    const storage = getBrowserLocalStorage();
    storage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch (error) {
    if (isBrowserStorageQuotaError(error)) {
      throw new AuthError(
        "browser storage quota exceeded",
        "SERVICE_ERROR",
        error,
      );
    }
    throw new AuthError(
      "Failed to write session to browser storage",
      "SERVICE_ERROR",
      error,
    );
  }
  return session;
}

function createSession(
  userId: string,
  user?: Omit<AuthUser, "id">,
): AuthSession {
  const session: AuthSession = {
    userId,
    sessionToken: crypto.randomUUID(),
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    user: user ? { id: userId, ...user } : null,
  };
  return saveSession(session);
}

function refreshStoredSessionUser(user: AuthUser): void {
  try {
    const storage = getBrowserLocalStorage();
    const raw = storage.getItem(SESSION_KEY);
    if (!raw) {
      return;
    }
    const session = readStoredSession(storage, raw);
    if (session.userId !== user.id) {
      return;
    }
    saveSession({
      ...session,
      user,
    });
  } catch (error) {
    if (error instanceof AuthError) {
      throw error;
    }
    throw new AuthError(
      "Failed to refresh session user in browser storage",
      "SERVICE_ERROR",
      error,
    );
  }
}

/**
 * Creates the mock browser-storage-backed authentication adapter used in demos and Storybook.
 *
 * @returns An auth adapter implementation that persists users and sessions to browser storage.
 * @throws {AuthError} When browser storage reads or writes fail in a recoverable auth flow.
 */
export function createLocalStorageAuthAdapter(): AuthAdapter {
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "production"
  ) {
    warnRuntimeAdapterMock(
      "[@geenius/adapters] browser storage auth adapter is a MOCK for prototyping only. " +
        "Do NOT use in production — passwords are not securely hashed. " +
        "Use a real auth provider from @geenius/auth instead.",
    );
  }

  return {
    async signInWithOAuth(provider, options) {
      const mockEmail = `oauth-${provider}-user@example.com`;
      const users = getUsers();
      let user = users.find((u) => u.email === mockEmail);
      if (!user) {
        user = {
          id: crypto.randomUUID(),
          email: mockEmail,
          name: `${provider.charAt(0).toUpperCase() + provider.slice(1)} User`,
          createdAt: new Date().toISOString(),
          passwordHash: "",
        };
        users.push(user);
        saveUsers(users);
      }
      createSession(user.id, toAuthUser(user));
      const redirectUrl = options?.redirectUrl || "/";
      return { url: redirectUrl };
    },

    async signUp(email, password, name) {
      const normalizedEmail = assertValidCredentials(email, password);
      const users = getUsers();
      if (users.find((u) => normalizeEmail(u.email) === normalizedEmail))
        throw new AuthError("User already exists", "USER_EXISTS");
      const user: StoredUser = {
        id: crypto.randomUUID(),
        email: normalizedEmail,
        name: name || normalizedEmail.split("@")[0],
        createdAt: new Date().toISOString(),
        passwordHash: simpleHash(password),
      };
      users.push(user);
      saveUsers(users);
      return createSession(user.id, {
        email: user.email,
        name: user.name,
        createdAt: user.createdAt,
      });
    },

    async signIn(email, password) {
      const normalizedEmail = normalizeEmail(email);
      const user = getUsers().find(
        (u) => normalizeEmail(u.email) === normalizedEmail,
      );
      if (!user || user.passwordHash !== simpleHash(password)) {
        throw new AuthError("Invalid email or password", "INVALID_CREDENTIALS");
      }
      assertUserCanAuthenticate(user);
      const authUser = toAuthUser(user);
      return createSession(user.id, authUser);
    },

    async signOut() {
      try {
        const storage = getBrowserLocalStorage();
        storage.removeItem(SESSION_KEY);
      } catch (error) {
        throw new AuthError(
          "Failed to clear session from browser storage",
          "SERVICE_ERROR",
          error,
        );
      }
    },

    async getSession() {
      try {
        const storage = getBrowserLocalStorage();
        const raw = storage.getItem(SESSION_KEY);
        if (!raw) return null;
        const s = readStoredSession(storage, raw);
        if (!s?.expiresAt) return null;
        const expiresAt = parseSessionExpiry(s.expiresAt);
        if (!expiresAt) {
          storage.removeItem(SESSION_KEY);
          throw new AuthError(
            "Invalid session expiry in browser storage",
            "SERVICE_ERROR",
          );
        }
        if (expiresAt < new Date()) {
          storage.removeItem(SESSION_KEY);
          return null;
        }
        const storedUser = getUsers().find((u) => u.id === s.userId);
        if (!storedUser) {
          storage.removeItem(SESSION_KEY);
          throw new AuthError("Session user no longer exists", "UNAUTHORIZED");
        }
        if (storedUser?.isBanned === true) {
          storage.removeItem(SESSION_KEY);
          assertUserCanAuthenticate(storedUser);
        }
        return s;
      } catch (error) {
        if (error instanceof AuthError) {
          throw error;
        }
        throw new AuthError(
          "Failed to read session from browser storage",
          "SERVICE_ERROR",
          error,
        );
      }
    },

    async getUser() {
      const session = await this.getSession();
      if (!session) return null;
      const stored = getUsers().find((u) => u.id === session.userId);
      if (!stored) return null;
      return toAuthUser(stored);
    },

    async updateUser(updates) {
      const session = await this.getSession();
      if (!session) {
        throw new AuthError("User is not authenticated", "UNAUTHORIZED");
      }
      const users = getUsers();
      const idx = users.findIndex((u) => u.id === session.userId);
      if (idx === -1) return null;
      const stored = users[idx];
      if (!stored) return null;
      Object.assign(stored, updates);
      saveUsers(users);
      const user = toAuthUser(stored);
      refreshStoredSessionUser(user);
      return user;
    },
  };
}
